import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { type StateSpec, stateToCss } from "../helpers";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

type VariantSpec = {
  enabled: StateSpec;
  hovered: StateSpec;
  focused: StateSpec;
  pressed: StateSpec;
  active: StateSpec;
  disabled: StateSpec;
  bordered?: boolean;
};

// `:active` and `[aria-pressed="true"]` are mutually-exclusive in our spec, and
// disabled buttons must never pick up hover/press styling — so each hover/press
// selector excludes both.
const MUI_SELECTORS = {
  hovered: '&:hover:not(:disabled, [aria-pressed="true"])',
  focused: "&:focus-visible",
  pressed: '&:active:not(:disabled, [aria-pressed="true"])',
  selected: '&[aria-pressed="true"]',
  disabled: "&:disabled, &.Mui-disabled",
};

const variantStyle =
  (build: (rmh: Rmh) => VariantSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      ...(spec.bordered && {
        borderWidth: "1.5px",
        borderStyle: "solid",
      }),
      ...stateToCss(spec.enabled),
      [MUI_SELECTORS.hovered]: stateToCss(spec.hovered),
      [MUI_SELECTORS.focused]: stateToCss(spec.focused),
      [MUI_SELECTORS.pressed]: stateToCss(spec.pressed),
      [MUI_SELECTORS.selected]: stateToCss(spec.active),
      [MUI_SELECTORS.disabled]: stateToCss(spec.disabled),
    };
  };

// --- variant spec factories ------------------------------------------------

const primaryContained = (rmh: Rmh): VariantSpec => ({
  enabled: {
    backgroundColor: rmh.paper["5-brandFilled"].rest,
    color: rmh.text.staticWhite,
  },
  hovered: {
    backgroundColor: rmh.paper["5-brandFilled"].hover,
    color: rmh.text.staticWhite,
  },
  focused: {
    backgroundColor: rmh.paper["5-brandFilled"].rest,
    color: rmh.text.staticWhite,
    ring: rmh.focus.neutral,
  },
  pressed: {
    backgroundColor: rmh.paper["5-brandFilled"].active,
    color: rmh.text.staticWhite,
  },
  active: {
    backgroundColor: rmh.paper["5-brandFilled"].active,
    color: rmh.text.staticWhite,
  },
  disabled: {
    backgroundColor: rmh.stateBackground.disabled,
    color: rmh.text.disabled,
  },
});

const primaryOutlined = (rmh: Rmh): VariantSpec => ({
  bordered: true,
  enabled: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["5-brand"].rest,
    color: rmh.text.interactiveLink,
  },
  hovered: {
    backgroundColor: rmh.paper["1-surface"].hover,
    borderColor: rmh.border["5-brand"].hover,
    color: rmh.text.interactiveLink,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["5-brand"].hover,
    color: rmh.text.interactiveLink,
    ring: rmh.focus.primary,
  },
  pressed: {
    backgroundColor: rmh.paper["1-surface"].active,
    borderColor: rmh.border["5-brand"].active,
    color: rmh.text.interactiveLink,
  },
  active: {
    backgroundColor: rmh.paper["5-brandFilled"].rest,
    borderColor: rmh.border["5-brand"].active,
    color: rmh.text.staticWhite,
  },
  disabled: {
    backgroundColor: rmh.stateBackground.disabledInput,
    borderColor: rmh.stateBorder.disabled,
    color: rmh.text.disabled,
  },
});

const primaryLight = (rmh: Rmh): VariantSpec => ({
  bordered: true,
  enabled: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["4-faint"].rest,
    color: rmh.text.interactiveLink,
  },
  hovered: {
    backgroundColor: rmh.paper["4-faint"].rest,
    borderColor: rmh.border["4-faint"].hover,
    color: rmh.text.interactiveLink,
  },
  focused: {
    backgroundColor: rmh.paper["4-faint"].rest,
    color: rmh.text.interactiveLink,
    ring: rmh.focus.neutral,
  },
  pressed: {
    backgroundColor: rmh.paper["4-faint"].active,
    borderColor: rmh.border["4-faint"].active,
    color: rmh.text.interactiveLink,
  },
  active: {
    backgroundColor: rmh.paper["7-brandFaint"].active,
    borderColor: rmh.border["7-brandFaint"].active,
    color: rmh.text.interactiveLink,
  },
  disabled: {
    backgroundColor: rmh.stateBackground.disabledInput,
    borderColor: rmh.stateBorder.disabled,
    color: rmh.text.disabled,
  },
});

const primaryText = (rmh: Rmh): VariantSpec => ({
  enabled: { backgroundColor: "transparent", color: rmh.text.interactiveLink },
  hovered: {
    backgroundColor: rmh.paper["1-surface"].hover,
    color: rmh.text.interactiveLink,
  },
  focused: {
    backgroundColor: "transparent",
    color: rmh.text.interactiveLink,
    ring: rmh.focus.primary,
  },
  pressed: { backgroundColor: "transparent", color: rmh.text.interactiveLink },
  active: { backgroundColor: "transparent", color: rmh.text.interactiveLink },
  disabled: { backgroundColor: "transparent", color: rmh.text.disabled },
});

const primaryLink = (rmh: Rmh): VariantSpec => ({
  enabled: {
    backgroundColor: "transparent",
    color: rmh.text.interactiveLink,
    textDecorationColor: rmh.text.interactiveLink,
  },
  hovered: {
    backgroundColor: rmh.paper["1-surface"].hover,
    color: rmh.text.interactiveLink,
    textDecorationColor: rmh.text.interactiveLink,
  },
  focused: {
    backgroundColor: "transparent",
    color: rmh.text.interactiveLink,
    ring: rmh.focus.primary,
    textDecorationColor: rmh.text.interactiveLink,
  },
  pressed: {
    backgroundColor: "transparent",
    color: rmh.text.interactiveLink,
    textDecorationColor: rmh.text.interactiveLink,
  },
  active: {
    backgroundColor: "transparent",
    color: rmh.text.interactiveLink,
    textDecorationColor: rmh.text.interactiveLink,
  },
  disabled: {
    backgroundColor: "transparent",
    color: rmh.text.disabled,
    textDecorationColor: rmh.text.disabled,
  },
});

const errorContained = (rmh: Rmh): VariantSpec => ({
  enabled: {
    backgroundColor: rmh.paper["8-errorFilled"].rest,
    color: rmh.text.staticWhite,
  },
  hovered: {
    backgroundColor: rmh.paper["8-errorFilled"].hover,
    color: rmh.text.staticWhite,
  },
  focused: {
    backgroundColor: rmh.paper["8-errorFilled"].rest,
    color: rmh.text.staticWhite,
    ring: rmh.focus.error,
  },
  pressed: {
    backgroundColor: rmh.paper["8-errorFilled"].active,
    color: rmh.text.staticWhite,
  },
  active: {
    backgroundColor: rmh.paper["8-errorFilled"].active,
    color: rmh.text.staticWhite,
  },
  disabled: {
    backgroundColor: rmh.stateBackground.disabled,
    color: rmh.text.disabled,
  },
});

const errorOutlined = (rmh: Rmh): VariantSpec => ({
  bordered: true,
  enabled: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["8-error"].rest,
    color: rmh.text.error,
  },
  hovered: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    borderColor: rmh.border["8-error"].hover,
    color: rmh.text.error,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["8-error"].hover,
    color: rmh.text.error,
    ring: rmh.focus.error,
  },
  pressed: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["8-error"].active,
    color: rmh.text.error,
  },
  active: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["8-error"].active,
    color: rmh.text.error,
  },
  disabled: {
    backgroundColor: rmh.stateBackground.disabledInput,
    borderColor: rmh.stateBorder.disabled,
    color: rmh.text.disabled,
  },
});

const errorText = (rmh: Rmh): VariantSpec => ({
  enabled: { backgroundColor: "transparent", color: rmh.text.error },
  hovered: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    color: rmh.text.error,
  },
  focused: {
    backgroundColor: "transparent",
    color: rmh.text.error,
    ring: rmh.focus.error,
  },
  pressed: { backgroundColor: "transparent", color: rmh.text.error },
  active: { backgroundColor: "transparent", color: rmh.text.error },
  disabled: { backgroundColor: "transparent", color: rmh.text.disabled },
});

const errorLink = (rmh: Rmh): VariantSpec => ({
  enabled: {
    backgroundColor: "transparent",
    color: rmh.text.error,
    textDecorationColor: rmh.text.error,
  },
  hovered: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    color: rmh.text.error,
    textDecorationColor: rmh.text.error,
  },
  focused: {
    backgroundColor: "transparent",
    color: rmh.text.error,
    ring: rmh.focus.error,
    textDecorationColor: rmh.text.error,
  },
  pressed: {
    backgroundColor: "transparent",
    color: rmh.text.error,
    textDecorationColor: rmh.text.error,
  },
  active: {
    backgroundColor: "transparent",
    color: rmh.text.error,
    textDecorationColor: rmh.text.error,
  },
  disabled: {
    backgroundColor: "transparent",
    color: rmh.text.disabled,
    textDecorationColor: rmh.text.disabled,
  },
});

// `color="inherit"` text buttons (e.g. the onboarding back arrow) cascade their
// color/hover from the surrounding context, so this variant adds ONLY the neutral
// focus ring and leaves every other state untouched — without it the button
// matches no spec and gets no design-system focus-visible styling at all.
const inheritText = (rmh: Rmh): VariantSpec => ({
  enabled: {},
  hovered: {},
  focused: { ring: rmh.focus.neutral },
  pressed: {},
  active: {},
  disabled: {},
});

// --- size variants ---------------------------------------------------------

const sizeVariants = [
  {
    props: { size: "xsmall" as const },
    style: ({ theme }: { theme: Theme }) => ({
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      ...theme.typography.bodySmMedium,
    }),
  },
  {
    props: { size: "small" as const },
    style: ({ theme }: { theme: Theme }) => ({
      padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
      ...theme.typography.bodyMdMedium,
    }),
  },
  {
    props: { size: "medium" as const },
    style: ({ theme }: { theme: Theme }) => ({
      padding: `${theme.spacing(2.5)} ${theme.spacing(4)}`,
      ...theme.typography.bodyMdMedium,
    }),
  },
  {
    props: { size: "large" as const },
    style: ({ theme }: { theme: Theme }) => ({
      padding: `${theme.spacing(3)} ${theme.spacing(6)}`,
      ...theme.typography.bodyLgMedium,
    }),
  },
];

// --- override --------------------------------------------------------------

export const MuiButton: NonNullable<Components<Theme>["MuiButton"]> = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    variant: "contained",
    color: "primary",
    size: "small",
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: RadiusToken.MD,
      textTransform: "none",
      boxShadow: "none",
      transition: theme.transitions.create(
        ["background-color", "border-color", "color", "box-shadow"],
        { duration: theme.transitions.duration.shortest }
      ),
      "&:hover": { boxShadow: "none" },
    }),
  },
  variants: [
    ...sizeVariants,
    {
      props: { variant: "contained", color: "primary" },
      style: variantStyle(primaryContained),
    },
    {
      props: { variant: "outlined", color: "primary" },
      style: variantStyle(primaryOutlined),
    },
    {
      props: { variant: "light", color: "primary" },
      style: variantStyle(primaryLight),
    },
    {
      props: { variant: "text", color: "primary" },
      style: variantStyle(primaryText),
    },
    {
      props: { variant: "link", color: "primary" },
      style: variantStyle(primaryLink),
    },
    {
      props: { variant: "contained", color: "error" },
      style: variantStyle(errorContained),
    },
    {
      props: { variant: "outlined", color: "error" },
      style: variantStyle(errorOutlined),
    },
    {
      props: { variant: "text", color: "error" },
      style: variantStyle(errorText),
    },
    {
      props: { variant: "link", color: "error" },
      style: variantStyle(errorLink),
    },
    {
      props: { variant: "text", color: "inherit" },
      style: variantStyle(inheritText),
    },
  ],
};
